import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class GoogleMapsLoaderService {
  private readonly document = inject(DOCUMENT);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly apiKey = environment.apiGoogleMapsKey;

  private loadPromise: Promise<void> | null = null;

  load(): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) {
      return Promise.resolve();
    }

    if (this.loadPromise) return this.loadPromise;

    const win = this.document.defaultView as any;

    if (typeof win?.google?.maps?.importLibrary === 'function') {
      this.loadPromise = this.loadMapLibraries(win);
      return this.loadPromise;
    }

    this.loadPromise = new Promise<void>((resolve, reject) => {
      const callbackName = '__territorioUnescoMapsReady';
      const script = this.document.createElement('script');
      win[callbackName] = () => {
        delete win[callbackName];
        this.loadMapLibraries(win).then(resolve, reject);
      };
      script.src =
        `https://maps.googleapis.com/maps/api/js` +
        `?key=${this.apiKey}` +
        `&loading=async` +
        `&callback=${callbackName}` +
        `&libraries=maps,marker`;
      script.async = true;
      script.defer = true;
      script.onerror = () => {
        delete win[callbackName];
        reject(new Error('Google Maps API could not load.'));
      };
      this.document.head.appendChild(script);
    });

    return this.loadPromise;
  }

  private async loadMapLibraries(win: Window & typeof globalThis): Promise<void> {
    await Promise.all([
      win.google.maps.importLibrary('maps'),
      win.google.maps.importLibrary('marker'),
    ]);
  }

  async importLibrary<T = any>(libraryName: string): Promise<T> {
    await this.load();
    const win = this.document.defaultView as any;
    return win.google.maps.importLibrary(libraryName) as Promise<T>;
  }
}
